import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { apiHeader, BaseURL } from '../../config/apiUrl';
import { Get, Post } from '../../Axios/AxiosFunctions';
import classes from './Comms.module.css';

const POLL_MS = 20000;

/**
 * Internal chat for everyone working this listing. Replaces the per-deal Zoom
 * group chats. Staff only: buyers and sellers never see it.
 */
const TeamChat = ({ listingId }) => {
  const { user, access_token: accessToken, company_id: companyId } = useSelector(
    (state) => state.authReducer,
  );
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  const load = useCallback(async () => {
    const res = await Get(BaseURL(`communications-feed/business/${listingId}/team-chat`), accessToken, companyId, false);
    if (res?.data?.data) setMessages(res.data.data);
  }, [listingId, accessToken, companyId]);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setSending(true);
    const res = await Post(
      BaseURL(`communications-feed/business/${listingId}/team-chat`),
      { text },
      apiHeader(accessToken),
      companyId,
    );
    setSending(false);
    if (res?.data?.data) {
      setDraft('');
      setMessages((m) => [...m, res.data.data]);
    }
  };

  const me = String(user?._id || '');

  return (
    <section className={classes.chat} aria-label="Team chat">
      <div className={classes.chatHead}>
        <span className={classes.itemTitle}>Team chat</span>
        <span className={classes.itemMeta}>Only BBS staff see this</span>
      </div>
      <div className={classes.chatList} ref={listRef}>
        {messages.length === 0 ? (
          <div className={classes.itemMeta}>No messages yet. Notes for the team about this deal go here.</div>
        ) : (
          messages.map((m) => (
            <div key={m.id} className={`${classes.chatMsg} ${m.author.id === me ? classes.chatMine : ''}`}>
              <div className={classes.itemMeta}>
                {m.author.id === me ? 'You' : m.author.name} · {moment(m.at).format('D MMM, h:mm a')}
              </div>
              <div className={classes.chatText}>{m.text}</div>
            </div>
          ))
        )}
      </div>
      <form className={classes.chatForm} onSubmit={send}>
        <textarea
          className={classes.valueInput}
          rows={2}
          value={draft}
          placeholder="Message the team"
          aria-label="Message the team"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) send(e);
          }}
        />
        <button type="submit" className={`${classes.actBtn} ${classes.actAccept}`} disabled={sending || !draft.trim()}>
          {sending ? 'Sending…' : 'Send'}
        </button>
      </form>
    </section>
  );
};

export default TeamChat;
